import { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { io, Socket } from 'socket.io-client';
import axios from 'axios';
import { API_URL } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import UserMenu from '../components/UserMenu';
import ChangePasswordModal from '../components/ChangePasswordModal';
import './KitchenDisplay.css';

interface Product {
  id: string;
  name: string;
  category: string;
}

interface OrderItem {
  id: string;
  quantity: number;
  product: Product;
  notes?: string | null;
  customization?: Record<string, string | string[]> | null;
}

interface Order {
  id: string;
  status: 'PENDING' | 'PREPARING' | 'READY' | 'COMPLETED' | 'CANCELLED';
  items: OrderItem[];
  createdAt: string;
  updatedAt?: string;
  notes?: string | null;
  restaurant: { name: string };
}

type FilterType = 'all' | 'queue' | 'cooking' | 'ready';

function KitchenDisplay() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [restaurantId, setRestaurantId] = useState<string>('');
  const [activeOrders, setActiveOrders] = useState<Order[]>([]);
  const [readyOrders, setReadyOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [, setSocket] = useState<Socket | null>(null);
  const [filter, setFilter] = useState<FilterType>('all');
  const [currentTime, setCurrentTime] = useState('');
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [avgPrepTimes, setAvgPrepTimes] = useState<Map<string, number>>(new Map());
  const avgRefreshRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const [overflowOpen, setOverflowOpen] = useState(false);
  const overflowRef = useRef<HTMLDivElement>(null);
  const [restaurantLogoUrl, setRestaurantLogoUrl] = useState<string | null>(null);
  const [upsellToast, setUpsellToast] = useState<{ orderId: string; text: string; ts: number } | null>(null);

  // Auto-dismiss upsell toast after 5s
  useEffect(() => {
    if (!upsellToast) return;
    const t = setTimeout(() => setUpsellToast(null), 5000);
    return () => clearTimeout(t);
  }, [upsellToast?.ts]);

  useEffect(() => {
    const tick = () => {
      const now = new Date();
      setCurrentTime(`${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`);
    };
    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (overflowRef.current && !overflowRef.current.contains(e.target as Node)) {
        setOverflowOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const rid = params.get('restaurantId');
    if (rid) {
      setRestaurantId(rid);
    } else {
      axios.get(`${API_URL}/api/restaurants`)
        .then(res => {
          if (res.data.length > 0) {
            setRestaurantId(res.data[0].id);
            setRestaurantLogoUrl(res.data[0]?.logoUrl || null);
          } else setError('kitchen.noRestaurants');
        })
        .catch(() => setError('kitchen.failedLoadRestaurants'));
    }
  }, []);

  useEffect(() => {
    if (!restaurantId) return;

    fetchOrders(false);
    let socketUrl = API_URL;
    if (!socketUrl) {
      const hostname = window.location.hostname;
      socketUrl = (hostname === 'localhost' || hostname === '127.0.0.1')
        ? 'http://localhost:3001'
        : `${window.location.protocol}//${hostname}:3001`;
    }
    const newSocket = io(socketUrl, { transports: ['websocket', 'polling'] });
    // Join restaurant room so we receive upsell-added / order-updated for our orders
    newSocket.emit('join-restaurant', restaurantId);

    newSocket.on('order-updated', (full: Order) => {
      if ((full as any).restaurantId !== restaurantId) return;
      setActiveOrders(prev => prev.map(o => o.id === full.id ? { ...o, ...full } : o));
      setReadyOrders(prev => prev.map(o => o.id === full.id ? { ...o, ...full } : o));
    });

    newSocket.on('upsell-added', (payload: { orderId: string; productName?: string; quantity: number; isVenue?: boolean; sourceOrderId?: string }) => {
      // Brief floating toast so the cook sees the update without scanning the whole list
      setUpsellToast({
        orderId: payload.orderId,
        text: payload.isVenue
          ? `+${payload.quantity}× ${payload.productName || 'позиция'} (venue, заказ ${payload.sourceOrderId?.slice(0, 8)})`
          : `+${payload.quantity}× ${payload.productName || 'позиция'} к заказу ${payload.orderId.slice(0, 8)}`,
        ts: Date.now(),
      });
      try { new Audio('/notification.mp3').play().catch(() => {}); } catch { /* */ }
    });

    newSocket.on('order-created', (newOrder: Order) => {
      if ((newOrder as any).restaurantId === restaurantId) {
        setActiveOrders(prev => [newOrder, ...prev]);
      }
    });

    newSocket.on('order-status-changed', ({ orderId, status }: { orderId: string; status: string }) => {
      if (status === 'READY') {
        setActiveOrders(prev => {
          const order = prev.find(o => o.id === orderId);
          if (order) {
            setReadyOrders(r => [{ ...order, status: 'READY' as const }, ...r]);
            return prev.filter(o => o.id !== orderId);
          }
          return prev;
        });
      } else if (status === 'COMPLETED' || status === 'CANCELLED') {
        setReadyOrders(prev => prev.filter(o => o.id !== orderId));
      } else {
        setActiveOrders(prev =>
          prev.map(o => o.id === orderId ? { ...o, status: status as Order['status'] } : o)
        );
      }
    });

    newSocket.on('analytics-update', () => {
      fetchAvgPrepTimes();
    });

    setSocket(newSocket);
    const interval = setInterval(() => fetchOrders(false), 10000);
    return () => {
      newSocket.close();
      clearInterval(interval);
    };
  }, [restaurantId]);

  useEffect(() => {
    if (!restaurantId) return;
    fetchAvgPrepTimes();
    avgRefreshRef.current = setInterval(fetchAvgPrepTimes, 10 * 60 * 1000);
    return () => {
      if (avgRefreshRef.current) clearInterval(avgRefreshRef.current);
    };
  }, [restaurantId]);

  const fetchOrders = async (showLoading = true) => {
    if (!restaurantId) return;
    if (showLoading) setLoading(true);
    setError('');
    try {
      const response = await axios.get(`${API_URL}/api/orders/restaurant/${restaurantId}`, {
        params: { limit: 100 },
      });
      const allOrders = (response.data.orders || []) as Order[];
      const active = allOrders.filter(o => o.status === 'PENDING' || o.status === 'PREPARING');
      const ready = allOrders.filter(o => o.status === 'READY');
      active.sort((a, b) => {
        if (a.status !== b.status) return a.status === 'PREPARING' ? -1 : 1;
        return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
      });
      ready.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
      setActiveOrders(active);
      setReadyOrders(ready);
    } catch (err: any) {
      setError(err.response?.data?.error || 'kitchen.failedLoadOrders');
    } finally {
      setLoading(false);
    }
  };

  const fetchAvgPrepTimes = async () => {
    if (!restaurantId) return;
    try {
      const res = await axios.get(`${API_URL}/api/analytics/restaurant/${restaurantId}/avg-prep-times`);
      const map = new Map<string, number>(
        (res.data as { productId: string; avgMinutes: number }[]).map(d => [d.productId, d.avgMinutes])
      );
      setAvgPrepTimes(map);
    } catch {
      // silently ignore — no SLA shown when unavailable
    }
  };

  const handleStatusChange = async (orderId: string, status: string) => {
    try {
      await axios.patch(`${API_URL}/api/orders/${orderId}/status`, { status });
    } catch (err: any) {
      setError(err.response?.data?.error || 'kitchen.failedUpdateStatus');
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const getStatusClass = (status: string) => {
    if (status === 'PENDING') return 'queue';
    if (status === 'PREPARING') return 'cooking';
    if (status === 'READY') return 'ready';
    return 'done';
  };

  const getTimeAgo = (dateStr: string) => {
    const mins = Math.floor((Date.now() - new Date(dateStr).getTime()) / 60000);
    if (mins < 1) return 'только что';
    return `${mins} мин назад`;
  };

  const getMinutesAgo = (dateStr: string) =>
    Math.floor((Date.now() - new Date(dateStr).getTime()) / 60000);

  const getOrderSLA = (order: Order): number | null => {
    const times = order.items
      .map(item => avgPrepTimes.get(item.product.id))
      .filter((t): t is number => t !== undefined);
    return times.length > 0 ? Math.max(...times) : null;
  };

  const getSLAColor = (ratio: number): 'green' | 'yellow' | 'red' => {
    if (ratio < 0.8) return 'green';
    if (ratio < 1.0) return 'yellow';
    return 'red';
  };

  const formatCustomization = (c: Record<string, string | string[]> | null | undefined): string => {
    if (!c || Object.keys(c).length === 0) return '';
    return Object.values(c).flat().filter(Boolean).join(', ');
  };

  const groupItemsByCategory = (items: OrderItem[], orderCreatedAt: string) => {
    const UPSELL_TIME_THRESHOLD = 5000;
    const orderTime = new Date(orderCreatedAt).getTime();
    const productGroups: Record<string, { product: Product; quantity: number; isUpsell: boolean; notes?: string | null; customization?: Record<string, string | string[]> | null }> = {};

    items.forEach(item => {
      const custKey = JSON.stringify(item.customization || {});
      const itemCreatedAt = (item as any).createdAt ? new Date((item as any).createdAt).getTime() : orderTime;
      const isUpsell = itemCreatedAt > orderTime + UPSELL_TIME_THRESHOLD;
      const key = `${item.product.id}::${custKey}::${isUpsell ? 'upsell' : 'original'}`;

      if (!productGroups[key]) {
        productGroups[key] = {
          product: item.product,
          quantity: 0,
          isUpsell,
          notes: item.notes,
          customization: item.customization,
        };
      }
      productGroups[key].quantity += item.quantity;
    });

    const grouped: Record<string, Array<{ product: Product; quantity: number; isUpsell: boolean; notes?: string | null; customization?: Record<string, string | string[]> | null }>> = {};
    Object.values(productGroups).forEach(group => {
      const category = group.product.category || 'Прочее';
      if (!grouped[category]) grouped[category] = [];
      grouped[category].push(group);
    });
    return grouped;
  };

  const allDisplayOrders = [...activeOrders, ...readyOrders];
  const queueCount = activeOrders.filter(o => o.status === 'PENDING').length;
  const cookingCount = activeOrders.filter(o => o.status === 'PREPARING').length;
  const readyCount = readyOrders.length;

  const filteredOrders = filter === 'all' ? allDisplayOrders
    : filter === 'queue' ? activeOrders.filter(o => o.status === 'PENDING')
    : filter === 'cooking' ? activeOrders.filter(o => o.status === 'PREPARING')
    : readyOrders;

  const restaurantName = activeOrders[0]?.restaurant?.name || readyOrders[0]?.restaurant?.name || 'OLSO';

  if (error && !restaurantId) {
    return (
      <div className="kitchen-container">
        <div className="kitchen-loading">
          <div className="kitchen-empty-icon"></div>
          <div>{t('common.error')}</div>
          <div>{t(error)}</div>
        </div>
      </div>
    );
  }

  if (!restaurantId) {
    return (
      <div className="kitchen-container">
        <div className="kitchen-loading">
          <div className="kitchen-empty-icon">⏳</div>
          <div>{t('common.loading')}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="kitchen-container">
      {upsellToast && (
        <div className="kitchen-upsell-toast" onClick={() => setUpsellToast(null)} role="status">
          <div className="kitchen-upsell-toast-title">{t('kitchen.upsellAddedTitle', 'Гость добавил позицию')}</div>
          <div className="kitchen-upsell-toast-body">{upsellToast.text}</div>
        </div>
      )}
      <div className="kitchen-header">
        <div className="kitchen-header-left">
          {restaurantLogoUrl
            ? <img src={restaurantLogoUrl} alt="logo" className="kitchen-logo kitchen-logo--img" />
            : <img src="/logo-olso.png" alt="OLSO" className="kitchen-logo-img" />
          }
          <div className="kitchen-restaurant">{restaurantName}</div>
        </div>

        <div className="kitchen-filters">
          <button className={`kitchen-filter-btn ${filter === 'all' ? 'active' : ''}`} onClick={() => setFilter('all')}>
            {t('kitchen.all')}
          </button>
          <button className={`kitchen-filter-btn ${filter === 'queue' ? 'active' : ''}`} onClick={() => setFilter('queue')}>
            {t('kitchen.queue')}
            {queueCount > 0 && <span className="kitchen-filter-count">{queueCount}</span>}
          </button>
          <button className={`kitchen-filter-btn ${filter === 'cooking' ? 'active' : ''}`} onClick={() => setFilter('cooking')}>
            {t('kitchen.cooking')}
            {cookingCount > 0 && <span className="kitchen-filter-count kitchen-filter-count--blue">{cookingCount}</span>}
          </button>
          <button className={`kitchen-filter-btn ${filter === 'ready' ? 'active' : ''}`} onClick={() => setFilter('ready')}>
            {t('kitchen.ready')}
            {readyCount > 0 && <span className="kitchen-filter-count kitchen-filter-count--green">{readyCount}</span>}
          </button>
        </div>

        <div className="kitchen-header-right">
          <span className="kitchen-clock">{currentTime}</span>
          <div className="kitchen-overflow-wrap" ref={overflowRef}>
            <button
              className="kitchen-overflow-btn"
              onClick={() => setOverflowOpen(o => !o)}
              aria-label="More options"
            >
              ⋯
            </button>
            {overflowOpen && (
              <div className="kitchen-overflow-menu">
                <button
                  className="kitchen-overflow-item"
                  onClick={() => { fetchOrders(); setOverflowOpen(false); }}
                  disabled={loading}
                >
                  ↻ {t('kitchen.refresh', 'Обновить')}
                </button>
                <button
                  className="kitchen-overflow-item"
                  onClick={() => { navigate('/admin'); setOverflowOpen(false); }}
                >
                  ← {t('kitchen.backToAdmin', 'В админку')}
                </button>
              </div>
            )}
          </div>
          <UserMenu
            name={user?.name || user?.email || 'Staff'}
            role={user?.role?.replace('_', ' ') || ''}
            restaurantName={restaurantName}
            onChangePassword={() => setShowChangePassword(true)}
            onLogout={handleLogout}
            theme="dark"
          />
        </div>
      </div>

      <div className="kitchen-grid">
        {loading && filteredOrders.length === 0 ? (
          <div className="kitchen-empty">
            <div className="kitchen-empty-icon">⏳</div>
            <div className="kitchen-empty-text">{t('kitchen.loadingOrders')}</div>
          </div>
        ) : filteredOrders.length === 0 ? (
          <div className="kitchen-empty">
            <div className="kitchen-empty-icon">‍</div>
            <div className="kitchen-empty-text">{t('kitchen.noOrders')}</div>
          </div>
        ) : (
          filteredOrders.map(order => {
            const sla = getOrderSLA(order);
            const elapsed = getMinutesAgo(order.createdAt);
            const ratio = sla ? elapsed / sla : null;
            const color = ratio !== null ? getSLAColor(ratio) : null;
            const breached = ratio !== null && ratio >= 1.0;
            const grouped = groupItemsByCategory(order.items, order.createdAt);
            const upsellItems = Object.values(grouped).flat().filter(i => i.isUpsell);
            return (
              <div key={order.id} className={`kitchen-card${breached ? ' sla-breached' : ''}`}>
                <div className={`kitchen-card-bar kitchen-card-bar--${getStatusClass(order.status)}`} />

                <div className="kitchen-card-header">
                  <div className="kitchen-card-id">#{order.id.slice(-6).toUpperCase()}</div>
                  <div className="kitchen-card-meta">
                    <div className="kitchen-card-time">{getTimeAgo(order.createdAt)}</div>
                    <div className={`kitchen-card-badge kitchen-card-badge--${getStatusClass(order.status)}`}>
                      {t(`kitchen.status.${order.status.toLowerCase()}`)}
                    </div>
                  </div>
                </div>

                {/* SLA Timer */}
                {order.status !== 'READY' && (
                  <div className="kitchen-timer">
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                      <div className="kitchen-timer-label">
                        {order.status === 'PREPARING' ? 'Готовка' : 'Ожидание'}
                      </div>
                      <span className={`kitchen-timer-value${color ? ` sla-${color}` : ''}`}>
                        {elapsed} мин
                      </span>
                    </div>
                    {sla !== null && (
                      <div className="pip-bar">
                        {Array.from({ length: 8 }, (_, i) => {
                          const threshold = (i + 1) / 8;
                          const filled = ratio !== null && ratio >= threshold;
                          return (
                            <div
                              key={i}
                              className={`pip-segment${filled ? ` filled-${color}` : ''}`}
                            />
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}

                <div className="kitchen-card-items">
                  {Object.entries(grouped).map(([category, items]) => (
                    <div key={category}>
                      <div className="kitchen-category-label">{category}</div>
                      {items.filter(i => !i.isUpsell).map((item, idx) => (
                        <div key={`${item.product.id}-${idx}`} className="kitchen-item-row">
                          <div className="kitchen-item-qty">{item.quantity}×</div>
                          <div className="kitchen-item-name">
                            {item.product.name}
                            {item.customization && Object.keys(item.customization).length > 0 && (
                              <span className="kitchen-item-mod">({formatCustomization(item.customization)})</span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ))}
                </div>

                {upsellItems.length > 0 && (
                  <div className="kitchen-upsells">
                    <div className="kitchen-upsells-label">+ {t('kitchen.upsells')}</div>
                    {upsellItems.map((u, idx) => (
                      <div key={`upsell-${idx}`} className="kitchen-upsell-item">
                        <span className="kitchen-upsell-qty">{u.quantity}×</span>
                        {u.product.name}
                      </div>
                    ))}
                  </div>
                )}

                {order.notes && (
                  <div className="kitchen-warning">
                     {order.notes}
                  </div>
                )}

                {order.status === 'READY' && order.updatedAt && getMinutesAgo(order.updatedAt) > 10 && (
                  <div className="kitchen-warning">
                     {t('kitchen.waitingWarning', { minutes: getMinutesAgo(order.updatedAt) })}
                  </div>
                )}

                <div className="kitchen-card-action">
                  {order.status === 'PENDING' && (
                    <button
                      className="kitchen-action-btn kitchen-action-btn--start"
                      onClick={() => handleStatusChange(order.id, 'PREPARING')}
                    >
                      ▶ {t('kitchen.startCooking')}
                    </button>
                  )}
                  {order.status === 'PREPARING' && (
                    <button
                      className="kitchen-action-btn kitchen-action-btn--ready"
                      onClick={() => handleStatusChange(order.id, 'READY')}
                    >
                       {t('kitchen.markReady')}
                    </button>
                  )}
                  {order.status === 'READY' && (
                    <button
                      className="kitchen-action-btn kitchen-action-btn--done"
                      onClick={() => handleStatusChange(order.id, 'COMPLETED')}
                    >
                       {t('kitchen.completed')}
                    </button>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>

      {showChangePassword && (
        <ChangePasswordModal
          onClose={() => setShowChangePassword(false)}
          onSuccess={() => setShowChangePassword(false)}
        />
      )}
    </div>
  );
}

export default KitchenDisplay;
